/** Behaviour / SHAPES dimensions. Keyed by slug (matches API column camelCase). */
export const order = ["achieving", "agreeable", "analytical", "autonomous", "behavioural", "competitive", "conceptual", "conscientious", "directing", "energetic", "focusedOnResults", "imaginative", "openToChange", "persuasive", "prudent", "sociable", "socially_confident", "systematic"];

const dictionary = {
  "en": {
    "achieving": {
      "id": "achieving",
      "title": "Achieving",
      "summary": "Measures the drive to pursue challenging goals, achieve personal success, and continuously develop.",
      "sampleItems": [
        "I like to work towards challenging targets",
        "I achieve difficult targets"
      ],
      "high": [
        "Enjoys striving towards stretching, challenging goals",
        "Is highly ambitious and driven",
        "Own career is a high priority; sees additional responsibilities as a reward",
        "Will probably tend to set demanding goals for others as well",
        "May sometimes risk team spirit or personal relations to progress in the organisation"
      ],
      "low": [
        "Sets achievable, realistic targets",
        "Appreciates an adequate work-life balance",
        "Own career is not a primary concern; prioritises other aspects, e.g. quality or delivery, above own career progression",
        "May feel overwhelmed easily with difficult challenges",
        "Runs the risk of not fully exploiting own skills or missing out on learning opportunities"
      ]
    },
    "agreeable": {
      "id": "agreeable",
      "title": "Agreeable",
      "summary": "Measures the preference for cooperation, coordination, and harmonious interaction.",
      "sampleItems": [
        "I co-operate well with others",
        "I believe in teamwork"
      ],
      "high": [
        "Enjoys contributing to the success of the team",
        "Shares information widely",
        "Fosters teamwork whenever possible",
        "Regards most people as reliable and trustworthy",
        "May be less effective when having to work alone"
      ],
      "low": [
        "Prefers to work independently and rarely feels the need for input from a team",
        "Tends to forget to share information and follows own speed",
        "Does not always see the advantages of teamwork",
        "Dislikes having to rely on others",
        "Tends to invest less effort when in a team and rarely contributes to a positive spirit"
      ]
    },
    "analytical": {
      "id": "analytical",
      "title": "Analytical",
      "summary": "Measures the tendency to examine information, data, and facts in detail and evaluate them rationally.",
      "sampleItems": [
        "I analyze information thoroughly",
        "I like working with numerical data"
      ],
      "high": [
        "Likes working with statistics and data",
        "Analyses information in-depth",
        "Looks for trends and patterns in various pieces of information",
        "Bases decisions on a number of different data, facts and information",
        "Feels less comfortable when having to decide without facts or in an insecure, new environment"
      ],
      "low": [
        "Relies on intuition or past experience when evaluating issues",
        "Does not need much data to form an opinion",
        "Takes a bit longer than others to analyse numbers, graphs, formulae",
        "Does not feel the need to back-up decisions with lots of facts and figures",
        "His/her judgement may be unfounded or superficial at times"
      ]
    },
    "autonomous": {
      "id": "autonomous",
      "title": "Autonomous",
      "summary": "Measures the preference for acting independently and basing decisions on one’s own beliefs and convictions.",
      "sampleItems": [
        "I follow my own direction",
        "I have an independent approach"
      ],
      "high": [
        "Trusts in own judgement and is very independent",
        "Takes the initiative and follows own views without asking for others’ opinions",
        "Makes own views known even if others disagree or feel offended",
        "Refuses to do things he/she is not convinced of",
        "May be seen as sovereign or stubborn or at times"
      ],
      "low": [
        "Likes to work with a safety-belt",
        "Is cautious about airing own opinions too openly",
        "Needs to have others’ support and prefers consensus or majority votes",
        "Sees self as being dependent on others in own work",
        "Feels quite insecure when having to decide on his/her own"
      ]
    },
    "behavioural": {
      "id": "behavioural",
      "title": "Behavioural",
      "summary": "Measures the interest in noticing others’ behavior and reactions and understanding their motives.",
      "sampleItems": [
        "I like to understand what motivates people",
        "I analyze others' behaviour"
      ],
      "high": [
        "Often reflects on others' motives and intentions",
        "Takes their likely reactions into account",
        "Observes and analyses others' reactions to own behaviours",
        "Tries to tailor own plans, ideas, arguments to counterparts or listeners"
      ],
      "low": [
        "Rarely takes time to analyse others' motives or intentions",
        "Is often surprised by others' reactions",
        "Focuses on tasks or results and is less interested in the 'people-side' of things",
        "Could tailor own plans and arguments more to counterparts"
      ]
    },
    "competitive": {
      "id": "competitive",
      "title": "Competitive",
      "summary": "Measures the preference for competition, performance comparison, and attaining a leading position.",
      "sampleItems": [
        "I enjoy competitive challenge",
        "I enjoy talking about my successes"
      ],
      "high": [
        "Values a sporty competition and believes it improves performance",
        "Wants to do better than others and constantly compares self in relation to others",
        "Keeps market competitors in watch and strives to be successful against them",
        "Own competitive edge may sometimes cause conflict or damage team spirit"
      ],
      "low": [
        "Is happy to share own successes with others",
        "Believes competition is detrimental for team harmony and atmosphere",
        "Wants to deliver good work, rather than better work",
        "Runs the risk of overlooking internal competition and tends not to be informed well about competitor activities"
      ]
    },
    "conceptual": {
      "id": "conceptual",
      "title": "Conceptual",
      "summary": "Measures the preference for abstract thinking, identifying connections, and engaging with conceptual questions.",
      "sampleItems": [
        "I like to understand the underlying concepts",
        "I like discussions about strategy"
      ],
      "high": [
        "Has strong conceptual capabilities and fun working with complex models",
        "Tends to be intellectually curious and enjoys a mental challenge",
        "Often takes a comprehensive, helicopter perspective",
        "May be seen as impractical or too theoretical in approach"
      ],
      "low": [
        "Prefers to work with concrete, pragmatic tasks",
        "Mostly sees abstract or theoretical discussions as useless",
        "Finds it difficult to see relations between issues and to identify the 'big picture'",
        "May have difficulties when having to build a vision or strategy for own business areas"
      ]
    },
    "conscientious": {
      "id": "conscientious",
      "title": "Conscientious",
      "summary": "Measures the orientation toward reliability, thoroughness, adherence to rules, and completing tasks in full.",
      "sampleItems": [
        "I make sure I meet deadlines",
        "I see jobs through to the end"
      ],
      "high": [
        "Values delivering high-quality work",
        "Likes to finish tasks before moving on to new ones",
        "Sees deadlines as fixed and believes rules to be important to maintain standards",
        "Is highly reliable and dependable",
        "Can sometimes come across as pedantic or dogmatic with regulations"
      ],
      "low": [
        "Initiates many issues, but tends to leave the details and completion to others",
        "Wants to move things forward and bends some rules if necessary",
        "Considers deadlines to be negotiable",
        "Complies with regulations if he/she considers them realistic or sensible",
        "May be seen as less reliable and accountable by others"
      ]
    },
    "directing": {
      "id": "directing",
      "title": "Directing",
      "summary": "Measures the preference for taking on leadership and responsibility within groups.",
      "sampleItems": [
        "I enjoy taking the lead",
        "I like to be in charge"
      ],
      "high": [
        "Enjoys directing people and taking responsibility for issues",
        "Co-ordinates a team to focus on targets",
        "Feels comfortable instructing others or organising the work for a team",
        "Likes to take the lead in new groups and among peers",
        "May come across as controlling and undemocratic in some situations"
      ],
      "low": [
        "Feels more comfortable when others take the lead",
        "Focuses on own tasks and objectives",
        "Dislikes telling others what to do; likely to go along with the given direction",
        "May feel tense when forced to take charge",
        "Prefers to share responsibility for decisions and direction-setting"
      ]
    },
    "energetic": {
      "id": "energetic",
      "title": "Energetic",
      "summary": "Measures the tendency to continue working consistently even in the face of setbacks, pressure, or tasks that take longer to complete.",
      "sampleItems": [
        "I pursue my work with a lot of drive and determination",
        "I invest a lot of time and energy in work"
      ],
      "high": [
        "Enjoys work most when there is a lot to do",
        "Has an energetic, dynamic approach",
        "Deals well with hectic periods and invests a lot of time in work",
        "Is willing to take on extra work; dislikes prolonged periods of inactivity",
        "Tends to take on too many commitments and responsibilities, so work-life balance may suffer"
      ],
      "low": [
        "Is concerned about demands becoming unbearable",
        "Dislikes having many simultaneous tasks, especially when there is little time to do them",
        "Feels less energetic than others and prefers a more tranquil or steady work pace",
        "May feel tired or exhausted when workload is high and needs some recovery times"
      ]
    },
    "focusedOnResults": {
      "id": "focusedOnResults",
      "title": "Focused On Results",
      "summary": "Measures the focus on achieving goals and results efficiently and pragmatically.",
      "sampleItems": [
        "I focus on reaching results",
        "I do whatever it takes to reach results"
      ],
      "high": [
        "Enjoys being responsible for reaching objectives and makes sure goals are achieved",
        "Wants to accomplish the task at hand in a quick, effective and pragmatic way",
        "Works with targets in mind and does whatever it takes to reaching them",
        "Tends to rush things to finish them up and may overlook important side issues"
      ],
      "low": [
        "Looks for the perfect solution and is willing to take detours into account",
        "Curious about side-aspects and often goes off at a tangent, which may lead to further insights",
        "Prefers to deliver bullet-proof results, eliminating all threats instead of rushing things",
        "May miss opportunities by not acting quickly enough or getting lost in details"
      ]
    },
    "imaginative": {
      "id": "imaginative",
      "title": "Imaginative",
      "summary": "Measures the preference for creative, unconventional, and innovative ideas and approaches to problem-solving.",
      "sampleItems": [
        "I produce a lot of new ideas",
        "I develop imaginative solutions"
      ],
      "high": [
        "Often comes up with new, fresh, creative insights",
        "Likes brainstorming tasks and contributes numerous ideas",
        "Enjoys working in creative, innovative environments",
        "Some of his/her ideas may be less practical or realistic",
        "May sometimes overwhelm others with the novelty and volume of ideas produced"
      ],
      "low": [
        "Develops quite conventional ideas",
        "Prefers to stick with well tried and tested solutions",
        "Is down-to-earth and considers self to be less creative",
        "More likely to develop and support others' ideas than come up with lots of own ideas",
        "Ideas are likely to build on previous work, rather than being completely new"
      ]
    },
    "openToChange": {
      "id": "openToChange",
      "title": "Open To Change",
      "summary": "Measures openness to new ideas, change, and alternative approaches.",
      "sampleItems": [
        "I enjoy exploring new ideas",
        "I am open to change"
      ],
      "high": [
        "Is excited about new possibilities and challenges",
        "Finds change invigorating and motivating",
        "Supports change initiatives and encourages others to do the same",
        "Adapts quickly to new circumstances and is flexible about working conditions",
        "May become restless or frustrated if change does not happen fast enough"
      ],
      "low": [
        "Prefers stability and clear direction",
        "Seeks to maintain the status quo and avoids drastic changes",
        "Needs time to adjust when something new is introduced",
        "May resist significant changes or feel uncomfortable with unanticipated developments"
      ]
    },
    "persuasive": {
      "id": "persuasive",
      "title": "Persuasive",
      "summary": "Measures the tendency to influence others through arguments and personal impact.",
      "sampleItems": [
        "I enjoy selling an idea",
        "I feel comfortable negotiating with people"
      ],
      "high": [
        "Enjoys promoting own plans and changing others’ views",
        "Builds convincing argumentation lines",
        "Likes getting involved in negotiations and sees self as being effective in them",
        "Feels at ease in cold-call sales situations",
        "May sometimes be seen as manipulative or cunning"
      ],
      "low": [
        "Relies strongly on facts and figures to promote plans or issues",
        "Tends to accept others' convictions quite readily without challenging them",
        "Gives people space to make up their own mind",
        "Feels uncomfortable in sales roles or in tough negotiations"
      ]
    },
    "prudent": {
      "id": "prudent",
      "title": "Prudent",
      "summary": "Measures the tendency to consider the potential consequences of one’s actions and take a forward-looking approach.",
      "sampleItems": [
        "I take a broad perspective",
        "I consider the wider consequences of plans and activities"
      ],
      "high": [
        "Considers the practicality of various solutions",
        "Detects advantages and disadvantages of a plan",
        "Identifies potential risks and takes a broad range of factors into account",
        "Always integrates a long-term view when building an opinion"
      ],
      "low": [
        "Rarely invests time to consider the long-term effects of actions",
        "Focuses more on short-term urgencies and is quite reactive and hasty",
        "Gives less thought to identifying potential risks or disadvantages of a plan",
        "Tends to overlook the broader implications of own actions"
      ]
    },
    "sociable": {
      "id": "sociable",
      "title": "Sociable",
      "summary": "Measures the preference for social interaction and contact with others.",
      "sampleItems": [
        "I enjoy meeting new people",
        "I feel comfortable in social situations"
      ],
      "high": [
        "Enjoys talking to others and is quite outgoing",
        "Has a wide network of contacts and keeps in touch",
        "Engages others in conversation easily and makes them feel comfortable",
        "May talk too much or dominate social events, occasionally leading to impatience in others"
      ],
      "low": [
        "Prefers smaller, more intimate groups",
        "Feels more comfortable one-on-one than in large groups",
        "Tends to stay quiet and not initiate conversation",
        "Does not feel a strong need for social interaction or building contacts"
      ]
    },
    "socially_confident": {
      "id": "socially_confident",
      "title": "Socially confident",
      "summary": "Measures perceived confidence and independence when interacting with others and in social situations.",
      "sampleItems": [
        "I am confident when meeting new people",
        "I feel at ease when presenting to others"
      ],
      "high": [
        "Feels comfortable taking on leadership roles in social situations",
        "Knows how to present ideas to large groups with confidence",
        "Feels comfortable in formal settings and enjoys social events",
        "Can come across as overly confident or even arrogant in some settings"
      ],
      "low": [
        "May struggle to speak up or present ideas in group settings",
        "Feels nervous or unsure when meeting new people or in formal situations",
        "Feels uncomfortable in large groups or public speaking settings"
      ]
    },
    "systematic": {
      "id": "systematic",
      "title": "Systematic",
      "summary": "Measures the preference for a structured approach, planning, and clear priorities.",
      "sampleItems": [
        "I like to plan ahead",
        "I am very organized"
      ],
      "high": [
        "Tends to plan things in detail and stay well-organized",
        "Sets realistic priorities and sticks to schedules",
        "Takes a logical and methodical approach to everything",
        "Can become too rigid or inflexible, which may cause delays when faced with changes"
      ],
      "low": [
        "Is more flexible and spontaneous in approach",
        "Does not like to be tied to rigid schedules",
        "Sometimes rushes through work without thinking it all through",
        "May not be as detailed or organized as others"
      ]
    }
  },
  "de": {
    "achieving": {
      "id": "achieving",
      "title": "Ehrgeizig",
      "summary": "Erfasst das Streben nach anspruchsvollen Zielen, persönlichem Erfolg und Weiterentwicklung.",
      "sampleItems": [
        "Ich arbeite gerne auf anspruchsvolle Ziele hin",
        "Ich erreiche schwierige Ziele"
      ],
      "high": [
        "Strebt gerne nach anspruchsvollen, herausfordernden Zielen",
        "Ist hochgradig ehrgeizig und antriebsstark",
        "Die eigene Karriere hat hohen Stellenwert; sieht zusätzliche Verantwortung als Anerkennung",
        "Neigt wahrscheinlich dazu, auch anderen anspruchsvolle Ziele zu setzen",
        "Kann Teamgeist oder persönliche Beziehungen gelegentlich zugunsten des eigenen Fortkommens in der Organisation gefährden"
      ],
      "low": [
        "Setzt erreichbare, realistische Ziele",
        "Schätzt eine angemessene Work-Life-Balance",
        "Die eigene Karriere steht nicht im Vordergrund; priorisiert andere Aspekte, z. B. Qualität oder Lieferung, vor dem eigenen beruflichen Aufstieg",
        "Kann von schwierigen Herausforderungen leicht überfordert sein",
        "Läuft Gefahr, die eigenen Fähigkeiten nicht voll auszuschöpfen oder Lernchancen zu verpassen"
      ]
    },
    "agreeable": {
      "id": "agreeable",
      "title": "Verträglich",
      "summary": "Erfasst die Präferenz für Kooperation, Abstimmung und ein harmonisches Miteinander.",
      "sampleItems": [
        "Ich arbeite gut mit anderen zusammen",
        "Ich glaube an Teamarbeit"
      ],
      "high": [
        "Trägt gerne zum Erfolg des Teams bei",
        "Teilt Informationen umfassend",
        "Fördert Teamarbeit, wo immer möglich",
        "Hält die meisten Menschen für zuverlässig und vertrauenswürdig",
        "Kann weniger wirksam sein, wenn allein gearbeitet werden muss"
      ],
      "low": [
        "Arbeitet lieber unabhängig und spürt selten das Bedürfnis nach Teambeiträgen",
        "Neigt dazu, das Teilen von Informationen zu vergessen und folgt dem eigenen Tempo",
        "Sieht nicht immer die Vorteile von Teamarbeit",
        "Mag es nicht, auf andere angewiesen zu sein",
        "Investiert im Team tendenziell weniger Aufwand und trägt selten zu einer positiven Atmosphäre bei"
      ]
    },
    "analytical": {
      "id": "analytical",
      "title": "Analysierend",
      "summary": "Erfasst die Tendenz, Informationen, Daten und Sachverhalte detailliert zu prüfen und rational zu bewerten.",
      "sampleItems": [
        "Ich analysiere Informationen gründlich",
        "Ich arbeite gerne mit Zahlenmaterial"
      ],
      "high": [
        "Arbeitet gerne mit Statistiken und Daten",
        "Analysiert Informationen eingehend",
        "Sucht in verschiedenen Informationen nach Trends und Mustern",
        "Stützt Entscheidungen auf eine Vielzahl von Daten, Fakten und Informationen",
        "Fühlt sich weniger wohl, wenn ohne Fakten oder in einem unsicheren, neuen Umfeld entschieden werden muss"
      ],
      "low": [
        "Verlässt sich bei der Bewertung von Sachverhalten auf Intuition oder frühere Erfahrung",
        "Braucht nicht viele Daten, um sich eine Meinung zu bilden",
        "Braucht etwas länger als andere, um Zahlen, Grafiken und Formeln zu analysieren",
        "Empfindet nicht die Notwendigkeit, Entscheidungen mit vielen Fakten und Zahlen zu untermauern",
        "Das Urteil kann gelegentlich unbegründet oder oberflächlich sein"
      ]
    },
    "autonomous": {
      "id": "autonomous",
      "title": "Unabhängig",
      "summary": "Erfasst die Präferenz, eigenständig zu handeln und sich bei Entscheidungen an den eigenen Überzeugungen zu orientieren.",
      "sampleItems": [
        "Ich folge meiner eigenen Richtung",
        "Ich gehe unabhängig vor"
      ],
      "high": [
        "Vertraut dem eigenen Urteil und ist sehr unabhängig",
        "Ergreift die Initiative und folgt den eigenen Ansichten, ohne die Meinung anderer einzuholen",
        "Macht die eigenen Ansichten auch dann kenntlich, wenn andere widersprechen oder sich verletzt fühlen",
        "Lehnt ab, Dinge zu tun, von denen er/sie nicht überzeugt ist",
        "Kann gelegentlich als souverän oder stur wahrgenommen werden"
      ],
      "low": [
        "Geht lieber auf Nummer sicher",
        "Ist zurückhaltend, eigene Meinungen allzu offen zu äußern",
        "Braucht die Unterstützung anderer und bevorzugt Konsens oder Mehrheitsentscheidungen",
        "Sieht sich in der eigenen Arbeit als abhängig von anderen",
        "Fühlt sich unsicher, wenn allein entschieden werden muss"
      ]
    },
    "behavioural": {
      "id": "behavioural",
      "title": "Beobachtend",
      "summary": "Erfasst das Interesse, Verhalten und Reaktionen anderer wahrzunehmen und deren Motive zu verstehen.",
      "sampleItems": [
        "Ich möchte verstehen, was Menschen motiviert",
        "Ich analysiere das Verhalten anderer"
      ],
      "high": [
        "Reflektiert häufig über Motive und Absichten anderer",
        "Berücksichtigt deren wahrscheinliche Reaktionen",
        "Beobachtet und analysiert die Reaktionen anderer auf das eigene Verhalten",
        "Versucht, eigene Pläne, Ideen und Argumente auf Gegenüber oder Zuhörende zuzuschneiden"
      ],
      "low": [
        "Nimmt sich selten Zeit, Motive oder Absichten anderer zu analysieren",
        "Ist oft von den Reaktionen anderer überrascht",
        "Konzentriert sich auf Aufgaben oder Ergebnisse und interessiert sich weniger für die menschliche Seite",
        "Könnte eigene Pläne und Argumente stärker auf das Gegenüber abstimmen"
      ]
    },
    "competitive": {
      "id": "competitive",
      "title": "Wettbewerbend",
      "summary": "Erfasst die Präferenz für Wettbewerb, Leistungsvergleich und das Erreichen einer führenden Position.",
      "sampleItems": [
        "Ich genieße wettkampforientierte Herausforderungen",
        "Ich spreche gerne über meine Erfolge"
      ],
      "high": [
        "Schätzt einen sportlichen Wettbewerb und ist überzeugt, dass die Leistung gesteigert werden kann",
        "Will besser sein als andere und vergleicht sich ständig mit anderen",
        "Behält Wettbewerber am Markt im Blick und strebt an, gegen sie erfolgreich zu sein",
        "Der eigene Wettbewerbsvorsprung kann gelegentlich Konflikte auslösen oder den Teamgeist beeinträchtigen"
      ],
      "low": [
        "Teilt eigene Erfolge gerne mit anderen",
        "Hält Wettbewerb für schädlich für Teamharmonie und Atmosphäre",
        "Will gute Arbeit liefern, nicht unbedingt bessere",
        "Läuft Gefahr, internen Wettbewerb zu übersehen, und ist tendenziell wenig über Aktivitäten der Wettbewerber informiert"
      ]
    },
    "conceptual": {
      "id": "conceptual",
      "title": "Konzeptionell",
      "summary": "Erfasst die Präferenz für abstraktes Denken, Zusammenhänge und konzeptionelle Fragestellungen.",
      "sampleItems": [
        "Ich möchte die zugrunde liegenden Konzepte verstehen",
        "Ich mag Diskussionen über Strategie"
      ],
      "high": [
        "Verfügt über starke konzeptionelle Fähigkeiten und hat Freude an komplexen Modellen",
        "Ist tendenziell intellektuell neugierig und genießt mentale Herausforderungen",
        "Nimmt häufig eine umfassende Vogelperspektive ein",
        "Kann als unpraktisch oder zu theoretisch im Vorgehen wahrgenommen werden"
      ],
      "low": [
        "Arbeitet lieber an konkreten, praxisnahen Aufgaben",
        "Hält abstrakte oder theoretische Diskussionen meist für nutzlos",
        "Fällt es schwer, Zusammenhänge zwischen Themen zu erkennen und das große Ganze zu sehen",
        "Kann Schwierigkeiten haben, eine Vision oder Strategie für die eigenen Geschäftsbereiche zu entwickeln"
      ]
    },
    "conscientious": {
      "id": "conscientious",
      "title": "Pflichtbewußt",
      "summary": "Erfasst die Orientierung an Verbindlichkeit, Sorgfalt, Regeln und der vollständigen Erledigung von Aufgaben.",
      "sampleItems": [
        "Ich stelle sicher, dass ich Fristen einhalte",
        "Ich führe Aufgaben zu Ende"
      ],
      "high": [
        "Legt Wert darauf, qualitativ hochwertige Arbeit zu liefern",
        "Schließt Aufgaben gerne ab, bevor neue begonnen werden",
        "Sieht Fristen als verbindlich an und hält Regeln für wichtig, um Standards zu sichern",
        "Ist höchst zuverlässig und verlässlich",
        "Kann bei Vorschriften gelegentlich pedantisch oder dogmatisch wirken"
      ],
      "low": [
        "Initiiert viele Themen, überlässt Details und Abschluss jedoch eher anderen",
        "Will Dinge voranbringen und beugt bei Bedarf Regeln",
        "Hält Fristen für verhandelbar",
        "Hält Vorschriften ein, wenn er/sie sie für realistisch oder sinnvoll hält",
        "Kann von anderen als weniger zuverlässig und verantwortlich wahrgenommen werden"
      ]
    },
    "directing": {
      "id": "directing",
      "title": "Leitend",
      "summary": "Erfasst die Präferenz, Führung und Verantwortung in Gruppen zu übernehmen.",
      "sampleItems": [
        "Ich übernehme gerne die Führung",
        "Ich möchte gerne die Leitung haben"
      ],
      "high": [
        "Führt gerne Menschen und übernimmt Verantwortung für Sachverhalte",
        "Koordiniert ein Team, damit es sich auf Ziele konzentriert",
        "Fühlt sich wohl dabei, andere anzuweisen oder die Arbeit eines Teams zu organisieren",
        "Übernimmt gerne die Führung in neuen Gruppen und unter Gleichgestellten",
        "Kann in manchen Situationen als kontrollierend und undemokratisch wahrgenommen werden"
      ],
      "low": [
        "Fühlt sich wohler, wenn andere die Führung übernehmen",
        "Konzentriert sich auf die eigenen Aufgaben und Ziele",
        "Mag es nicht, anderen zu sagen, was sie tun sollen; folgt wahrscheinlich der vorgegebenen Richtung",
        "Kann unter Anspannung stehen, wenn die Leitung übernommen werden muss",
        "Teilt lieber die Verantwortung für Entscheidungen und Richtungsvorgaben"
      ]
    },
    "energetic": {
      "id": "energetic",
      "title": "Ausdauernd",
      "summary": "Erfasst die Tendenz, auch bei Rückschlägen, Belastung oder länger dauernden Aufgaben kontinuierlich weiterzuarbeiten.",
      "sampleItems": [
        "Ich verfolge meine Arbeit mit viel Antrieb und Entschlossenheit",
        "Ich investiere viel Zeit und Energie in die Arbeit"
      ],
      "high": [
        "Arbeitet am liebsten, wenn viel zu tun ist",
        "Hat einen energiegeladenen, dynamischen Ansatz",
        "Kommt mit hektischen Phasen gut zurecht und investiert viel Zeit in die Arbeit",
        "Übernimmt gerne zusätzliche Arbeit; mag längere Phasen der Inaktivität nicht",
        "Neigt dazu, zu viele Verpflichtungen und Verantwortlichkeiten zu übernehmen, sodass die Work-Life-Balance leiden kann"
      ],
      "low": [
        "Sorgt sich, dass Anforderungen unerträglich werden könnten",
        "Mag es nicht, viele Aufgaben gleichzeitig zu haben, vor allem wenn wenig Zeit bleibt",
        "Fühlt sich weniger energiegeladen als andere und bevorzugt ein ruhigeres oder gleichmäßigeres Arbeitstempo",
        "Kann bei hoher Arbeitslast müde oder erschöpft sein und braucht Erholungszeiten"
      ]
    },
    "focusedOnResults": {
      "id": "focusedOnResults",
      "title": "Ergebnisorientiert",
      "summary": "Erfasst die Ausrichtung auf die effiziente und pragmatische Erreichung von Zielen und Ergebnissen.",
      "sampleItems": [
        "Ich konzentriere mich auf das Erreichen von Ergebnissen",
        "Ich tue, was nötig ist, um Ergebnisse zu erzielen"
      ],
      "high": [
        "Übernimmt gerne die Verantwortung für die Zielerreichung und stellt sicher, dass Ziele erreicht werden",
        "Will die anstehende Aufgabe schnell, wirksam und pragmatisch erledigen",
        "Arbeitet mit Zielen vor Augen und tut, was nötig ist, um sie zu erreichen",
        "Neigt dazu, Dinge zu überstürzen, um sie abzuschließen, und kann wichtige Nebenaspekte übersehen"
      ],
      "low": [
        "Sucht die perfekte Lösung und ist bereit, Umwege in Kauf zu nehmen",
        "Ist neugierig auf Nebenaspekte und schweift oft ab, was zu weiteren Erkenntnissen führen kann",
        "Liefert lieber wasserdichte Ergebnisse und beseitigt alle Risiken, statt Dinge zu überstürzen",
        "Kann Chancen verpassen, indem nicht schnell genug gehandelt oder in Details verloren wird"
      ]
    },
    "imaginative": {
      "id": "imaginative",
      "title": "Einfallsreich",
      "summary": "Erfasst die Präferenz für kreative, ungewöhnliche und innovative Ideen und Lösungsansätze.",
      "sampleItems": [
        "Ich entwickle viele neue Ideen",
        "Ich entwickle einfallsreiche Lösungen"
      ],
      "high": [
        "Kommt häufig auf neue, frische, kreative Einsichten",
        "Mag Brainstorming-Aufgaben und steuert zahlreiche Ideen bei",
        "Arbeitet gerne in kreativen, innovativen Umfeldern",
        "Manche Ideen können weniger praktisch oder realistisch sein",
        "Kann andere gelegentlich mit Neuheit und Menge der hervorgebrachten Ideen überfordern"
      ],
      "low": [
        "Entwickelt eher konventionelle Ideen",
        "Hält sich lieber an bewährte und erprobte Lösungen",
        "Ist bodenständig und hält sich selbst für weniger kreativ",
        "Entwickelt und unterstützt eher die Ideen anderer, als selbst viele eigene Ideen einzubringen",
        "Ideen bauen eher auf früherer Arbeit auf, als vollständig neu zu sein"
      ]
    },
    "openToChange": {
      "id": "openToChange",
      "title": "Offen für Veränderungen",
      "summary": "Erfasst die Offenheit gegenüber neuen Ideen, Veränderungen und alternativen Vorgehensweisen.",
      "sampleItems": [
        "Ich erkunde gerne neue Ideen",
        "Ich bin offen für Veränderungen"
      ],
      "high": [
        "Begeistert sich für neue Möglichkeiten und Herausforderungen",
        "Empfindet Veränderung als belebend und motivierend",
        "Unterstützt Veränderungsinitiativen und ermutigt andere, dasselbe zu tun",
        "Passt sich schnell an neue Umstände an und ist flexibel gegenüber Arbeitsbedingungen",
        "Kann unruhig oder frustriert werden, wenn Veränderung nicht schnell genug eintritt"
      ],
      "low": [
        "Bevorzugt Stabilität und klare Richtung",
        "Strebt danach, den Status quo zu erhalten, und vermeidet einschneidende Veränderungen",
        "Braucht Zeit, um sich anzupassen, wenn etwas Neues eingeführt wird",
        "Kann sich erheblichen Veränderungen widersetzen oder sich bei unerwarteten Entwicklungen unwohl fühlen"
      ]
    },
    "persuasive": {
      "id": "persuasive",
      "title": "Überzeugend",
      "summary": "Erfasst die Tendenz, andere durch Argumente und persönliche Wirkung zu beeinflussen.",
      "sampleItems": [
        "Ich verkaufe gerne eine Idee",
        "Ich fühle mich wohl dabei, mit Menschen zu verhandeln"
      ],
      "high": [
        "Wirbt gerne für eigene Pläne und verändert die Ansichten anderer",
        "Baut überzeugende Argumentationslinien auf",
        "Beteiligt sich gerne an Verhandlungen und sieht sich darin als wirksam",
        "Fühlt sich in Kaltakquise-Verkaufssituationen wohl",
        "Kann gelegentlich als manipulativ oder durchtrieben wahrgenommen werden"
      ],
      "low": [
        "Stützt sich stark auf Fakten und Zahlen, um Pläne oder Themen zu vertreten",
        "Akzeptiert die Überzeugungen anderer recht bereitwillig, ohne sie infrage zu stellen",
        "Gibt Menschen Raum, sich selbst eine Meinung zu bilden",
        "Fühlt sich in Vertriebsrollen oder harten Verhandlungen unwohl"
      ]
    },
    "prudent": {
      "id": "prudent",
      "title": "Umsichtig",
      "summary": "Erfasst die Tendenz, mögliche Folgen des eigenen Handelns zu berücksichtigen und vorausschauend vorzugehen.",
      "sampleItems": [
        "Ich nehme eine weite Perspektive ein",
        "Ich berücksichtige die weiterreichenden Folgen von Plänen und Aktivitäten"
      ],
      "high": [
        "Prüft die Umsetzbarkeit verschiedener Lösungen",
        "Erkennt Vor- und Nachteile eines Plans",
        "Identifiziert potenzielle Risiken und berücksichtigt ein breites Spektrum an Faktoren",
        "Bezieht bei der Meinungsbildung stets eine langfristige Sicht ein"
      ],
      "low": [
        "Investiert selten Zeit, die langfristigen Auswirkungen von Handlungen zu bedenken",
        "Konzentriert sich stärker auf kurzfristige Dringlichkeiten und handelt recht reaktiv und hastig",
        "Denkt weniger daran, potenzielle Risiken oder Nachteile eines Plans zu identifizieren",
        "Neigt dazu, die weiterreichenden Implikationen des eigenen Handelns zu übersehen"
      ]
    },
    "sociable": {
      "id": "sociable",
      "title": "Kontaktfreudig",
      "summary": "Erfasst die Präferenz für sozialen Austausch und den Kontakt mit anderen.",
      "sampleItems": [
        "Ich treffe gerne neue Menschen",
        "Ich fühle mich in sozialen Situationen wohl"
      ],
      "high": [
        "Spricht gerne mit anderen und ist recht extravertiert",
        "Verfügt über ein großes Kontaktnetz und bleibt in Verbindung",
        "Zieht andere leicht ins Gespräch und lässt sie sich wohlfühlen",
        "Kann zu viel sprechen oder soziale Anlässe dominieren, was bei anderen gelegentlich Ungeduld auslöst"
      ],
      "low": [
        "Bevorzugt kleinere, intimere Gruppen",
        "Fühlt sich im Einzelgespräch wohler als in großen Gruppen",
        "Bleibt eher still und beginnt Gespräche nicht von sich aus",
        "Empfindet kein starkes Bedürfnis nach sozialer Interaktion oder dem Aufbau von Kontakten"
      ]
    },
    "socially_confident": {
      "id": "socially_confident",
      "title": "Souverän",
      "summary": "Erfasst die wahrgenommene Sicherheit und Selbstständigkeit im Umgang mit anderen und in sozialen Situationen.",
      "sampleItems": [
        "Ich bin selbstsicher, wenn ich neue Menschen treffe",
        "Ich fühle mich wohl, wenn ich vor anderen präsentiere"
      ],
      "high": [
        "Fühlt sich wohl, in sozialen Situationen Führungsrollen zu übernehmen",
        "Weiß, wie Ideen selbstsicher vor großen Gruppen präsentiert werden",
        "Fühlt sich in formellen Settings wohl und genießt gesellschaftliche Anlässe",
        "Kann in manchen Situationen als übermäßig selbstsicher oder sogar arrogant wirken"
      ],
      "low": [
        "Kann Schwierigkeiten haben, in Gruppensituationen das Wort zu ergreifen oder Ideen vorzustellen",
        "Fühlt sich nervös oder unsicher, wenn neue Menschen getroffen werden oder in formellen Situationen",
        "Fühlt sich in großen Gruppen oder bei öffentlichem Sprechen unwohl"
      ]
    },
    "systematic": {
      "id": "systematic",
      "title": "Planvoll",
      "summary": "Erfasst die Präferenz für strukturiertes Vorgehen, Planung und klare Prioritäten.",
      "sampleItems": [
        "Ich plane gerne voraus",
        "Ich bin sehr organisiert"
      ],
      "high": [
        "Plant Dinge tendenziell im Detail und bleibt gut organisiert",
        "Setzt realistische Prioritäten und hält Zeitpläne ein",
        "Geht bei allem logisch und methodisch vor",
        "Kann zu starr oder unflexibel werden, was bei Veränderungen zu Verzögerungen führen kann"
      ],
      "low": [
        "Geht flexibler und spontaner vor",
        "Mag es nicht, an starre Zeitpläne gebunden zu sein",
        "Erledigt Arbeit manchmal überstürzt, ohne alles durchzudenken",
        "Ist möglicherweise weniger detailliert oder organisiert als andere"
      ]
    }
  },
  "fi": {
    "achieving": {
      "id": "achieving",
      "title": "Tavoitteellisuus",
      "summary": "Mittaa halua tavoitella haastavia tavoitteita, saavuttaa henkilökohtaista menestystä ja kehittyä jatkuvasti.",
      "sampleItems": [
        "Työskentelen mielelläni haastavia tavoitteita kohti",
        "Saavutan vaikeita tavoitteita"
      ],
      "high": [
        "Nauttii kunnianhimoisten ja haastavien tavoitteiden tavoittelusta",
        "On erittäin kunnianhimoinen ja määrätietoinen",
        "Pitää omaa uraansa hyvin tärkeänä; näkee lisävastuun palkintona",
        "Asettaa todennäköisesti myös muille vaativia tavoitteita",
        "Saattaa joskus vaarantaa tiimihengen tai henkilökohtaiset suhteet edetäkseen organisaatiossa"
      ],
      "low": [
        "Asettaa saavutettavia, realistisia tavoitteita",
        "Arvostaa riittävää työn ja yksityiselämän tasapainoa",
        "Oma ura ei ole hänelle ensisijainen; asettaa muut asiat, esimerkiksi laadun tai toimitukset, oman urakehityksen edelle",
        "Voi helposti kuormittua liikaa vaikeiden haasteiden edessä",
        "Vaarana on, ettei hän hyödynnä omia taitojaan täysin tai jää paitsi oppimismahdollisuuksista"
      ]
    },
    "agreeable": {
      "id": "agreeable",
      "title": "Yhteistyöhakuinen",
      "summary": "Mittaa mieltymystä yhteistyöhön, koordinointiin ja harmoniseen vuorovaikutukseen.",
      "sampleItems": [
        "Teen hyvin yhteistyötä muiden kanssa",
        "Uskon tiimityöhön"
      ],
      "high": [
        "Nauttii siitä, että voi edistää tiimin menestystä",
        "Jakaa tietoa laajasti",
        "Edistää tiimityötä aina kun mahdollista",
        "Pitää useimpia ihmisiä luotettavina ja rehellisinä",
        "Voi olla vähemmän tehokas työskennellessään yksin"
      ],
      "low": [
        "Haluaa työskennellä itsenäisesti ja kaipaa harvoin tiimin näkemyksiä",
        "Unohtaa helposti jakaa tietoa ja etenee omaa tahtiaan",
        "Ei aina näe tiimityön etuja",
        "Ei pidä siitä, että joutuu olemaan riippuvainen muista",
        "Panostaa yleensä vähemmän tiimissä ja osallistuu harvoin positiivisen ilmapiirin luomiseen"
      ]
    },
    "analytical": {
      "id": "analytical",
      "title": "Analyyttinen",
      "summary": "Mittaa taipumusta tarkastella tietoa, dataa ja faktoja yksityiskohtaisesti ja arvioida niitä rationaalisesti.",
      "sampleItems": [
        "Analysoin tietoa perusteellisesti",
        "Pidän työskentelystä numeerisen datan parissa"
      ],
      "high": [
        "Pitää työskentelystä tilastojen ja datan parissa",
        "Analysoi tietoa syvällisesti",
        "Etsii trendejä ja toistuvia kaavoja erilaisista tiedoista",
        "Perustaa päätöksensä monenlaiseen dataan, faktoihin ja tietoon",
        "Tuntee olonsa epämukavammaksi, kun joutuu päättämään asioista ilman faktoja tai epävarmassa, uudessa ympäristössä"
      ],
      "low": [
        "Luottaa asioita arvioidessaan intuitioon tai aiempaan kokemukseen",
        "Ei tarvitse paljon tietoa muodostaakseen mielipiteen",
        "Analysoi lukuja, kaavioita ja kaavoja hieman muita hitaammin",
        "Ei koe tarvetta tukea päätöksiään runsailla faktoilla ja luvuilla",
        "Hänen arvionsa voi toisinaan olla perusteeton tai pinnallinen"
      ]
    },
    "autonomous": {
      "id": "autonomous",
      "title": "Itsenäinen",
      "summary": "Mittaa mieltymystä toimia itsenäisesti ja perustaa päätökset omiin uskomuksiin ja vakaumuksiin.",
      "sampleItems": [
        "Seuraan omaa suuntaani",
        "Toimin itsenäisesti"
      ],
      "high": [
        "Luottaa omaan arvioonsa ja on hyvin itsenäinen",
        "Tekee aloitteita ja toimii omien näkemystensä mukaan kysymättä muiden mielipiteitä",
        "Tuo omat näkemyksensä esille, vaikka muut olisivat eri mieltä tai loukkaantuisivat",
        "Kieltäytyy tekemästä asioita, joista hän ei ole vakuuttunut",
        "Saattaa joskus vaikuttaa omapäiseltä tai itsepäiseltä"
      ],
      "low": [
        "Työskentelee mieluiten turvaverkon varassa",
        "Varoo tuomasta omia mielipiteitään liian avoimesti esiin",
        "Tarvitsee muiden tukea ja suosii yksimielisyyttä tai enemmistöpäätöksiä",
        "Näkee itsensä riippuvaisena muista omassa työssään",
        "Tuntee olonsa melko epävarmaksi, kun hänen täytyy päättää yksin"
      ]
    },
    "behavioural": {
      "id": "behavioural",
      "title": "Ihmisiä analysoiva",
      "summary": "Mittaa kiinnostusta havainnoida muiden käyttäytymistä ja reaktioita sekä ymmärtää heidän motiivejaan.",
      "sampleItems": [
        "Pidän siitä, että ymmärrän, mikä motivoi ihmisiä",
        "Analysoin muiden käyttäytymistä"
      ],
      "high": [
        "Pohtii usein muiden motiiveja ja aikeita",
        "Ottaa huomioon heidän todennäköiset reaktionsa",
        "Havainnoi ja analysoi muiden reaktioita omaan käyttäytymiseensä",
        "Pyrkii mukauttamaan omia suunnitelmiaan, ideoitaan ja argumenttejaan keskustelukumppanien tai kuulijoiden mukaan"
      ],
      "low": [
        "Käyttää harvoin aikaa muiden motiivien tai aikomusten analysointiin",
        "On usein yllättynyt muiden reaktioista",
        "Keskittyy tehtäviin tai tuloksiin ja on vähemmän kiinnostunut asioiden inhimillisestä puolesta",
        "Voisi mukauttaa omia suunnitelmiaan ja argumenttejaan enemmän keskustelukumppanien mukaan"
      ]
    },
    "competitive": {
      "id": "competitive",
      "title": "Kilpailuhenkinen",
      "summary": "Mittaa mieltymystä kilpailuun, suoritusten vertailuun ja johtavan aseman saavuttamiseen.",
      "sampleItems": [
        "Nautin kilpailun tuomasta haasteesta",
        "Nautin omista saavutuksistani puhumisesta"
      ],
      "high": [
        "Arvostaa urheiluhenkistä kilpailua ja uskoo sen parantavan suorituksia",
        "Haluaa menestyä paremmin kuin muut ja vertailee jatkuvasti itseään muihin",
        "Seuraa kilpailijoita markkinoilla ja pyrkii menestymään niitä paremmin",
        "Vahva kilpailuhenkisyys saattaa joskus aiheuttaa ristiriitoja tai heikentää tiimihenkeä"
      ],
      "low": [
        "Jakaa mielellään omia onnistumisiaan muiden kanssa",
        "Uskoo, että kilpailu on haitallista tiimiharmonialle ja -ilmapiirille",
        "Haluaa tehdä hyvää työtä sen sijaan, että pyrkisi aina vielä parempaan",
        "Saattaa jättää sisäisen kilpailun huomiotta eikä yleensä ole kovin hyvin perillä kilpailijoiden toimista"
      ]
    },
    "conceptual": {
      "id": "conceptual",
      "title": "Käsitteellinen",
      "summary": "Mittaa mieltymystä abstraktiin ajatteluun, yhteyksien tunnistamiseen ja käsitteellisten kysymysten pohdintaan.",
      "sampleItems": [
        "Haluan ymmärtää taustalla olevat käsitteet",
        "Pidän strategiakeskusteluista"
      ],
      "high": [
        "On vahva käsitteellinen ajattelija ja nauttii työskentelystä monimutkaisten mallien parissa",
        "On yleensä älyllisesti utelias ja nauttii älyllisistä haasteista",
        "Tarkastelee asioita usein kokonaisvaltaisesti, niin sanotusti helikopteriperspektiivistä",
        "Muut saattavat pitää hänen lähestymistapaansa epäkäytännöllisenä tai liian teoreettisena"
      ],
      "low": [
        "Työskentelee mieluiten konkreettisten ja käytännönläheisten tehtävien parissa",
        "Pitää abstrakteja tai teoreettisia keskusteluja useimmiten hyödyttöminä",
        "Hänen on vaikea nähdä asioiden välisiä yhteyksiä ja hahmottaa kokonaiskuvaa",
        "Hänen voi olla vaikea luoda visiota tai strategiaa omille liiketoiminta-alueilleen"
      ]
    },
    "conscientious": {
      "id": "conscientious",
      "title": "Tunnollinen",
      "summary": "Mittaa suuntautumista luotettavuuteen, huolellisuuteen, sääntöjen noudattamiseen ja tehtävien täydelliseen suorittamiseen.",
      "sampleItems": [
        "Pidän huolen siitä, että pysyn aikatauluissa",
        "Vien työtehtävät aina loppuun asti"
      ],
      "high": [
        "Arvostaa korkealaatuista työtä",
        "Haluaa saada tehtävät valmiiksi ennen kuin siirtyy uusiin",
        "Pitää määräaikoja ehdottomina ja uskoo, että sääntöjä tarvitaan standardien ylläpitämiseen",
        "On erittäin luotettava ja tunnollinen",
        "Voi joskus vaikuttaa sääntöjen suhteen pedanttiselta tai dogmaattiselta"
      ],
      "low": [
        "Aloittaa monia asioita, mutta jättää yksityiskohdat ja loppuun saattamisen usein muille",
        "Haluaa viedä asioita eteenpäin ja joustaa tarvittaessa säännöistä",
        "Pitää määräaikoja neuvoteltavina",
        "Noudattaa sääntöjä, jos hän pitää niitä realistisina tai järkevinä",
        "Muut saattavat pitää häntä vähemmän luotettavana ja vastuullisena"
      ]
    },
    "directing": {
      "id": "directing",
      "title": "Ohjaava",
      "summary": "Mittaa mieltymystä ottaa johtajuutta ja vastuuta ryhmässä.",
      "sampleItems": [
        "Pidän siitä, että saan olla johtajana",
        "Haluan olla johdossa"
      ],
      "high": [
        "Nauttii ihmisten ohjaamisesta ja vastuun kantamisesta",
        "Koordinoi tiimin työtä niin, että se keskittyy tavoitteisiin",
        "Ohjaa luontevasti muita ja organisoi tiimin työtä",
        "Ottaa mielellään johtavan roolin uusissa ryhmissä ja vertaistensa joukossa",
        "Voi vaikuttaa hallitsevalta ja epädemokraattiselta joissakin tilanteissa"
      ],
      "low": [
        "Tuntee olonsa mukavammaksi, kun johtovastuu on jollakulla muulla",
        "Keskittyy omiin tehtäviinsä ja tavoitteisiinsa",
        "Ei pidä siitä, että joutuu käskemään muita; seuraa todennäköisesti annettua suuntaa",
        "Voi jännittää, jos joutuu ottamaan johtajan roolin",
        "Jakaa mieluiten vastuun päätöksistä ja suunnan määrittämisestä"
      ]
    },
    "energetic": {
      "id": "energetic",
      "title": "Energinen",
      "summary": "Mittaa taipumusta jatkaa työskentelyä johdonmukaisesti myös vastoinkäymisistä, paineesta tai pitkäkestoisista tehtävistä huolimatta.",
      "sampleItems": [
        "Teen työtäni suurella tarmolla ja päättäväisyydellä",
        "Panostan työhön paljon aikaa ja energiaa"
      ],
      "high": [
        "Nauttii työstä eniten, kun tekemistä on paljon",
        "Toimii energisesti ja dynaamisesti",
        "Selviää hyvin hektisistä jaksoista ja käyttää työhön paljon aikaa",
        "On valmis ottamaan lisätöitä; ei pidä pitkistä toimettomista jaksoista",
        "Ottaa helposti liikaa sitoumuksia ja vastuuta, jolloin työn ja yksityiselämän tasapaino voi kärsiä"
      ],
      "low": [
        "On huolissaan siitä, että vaatimukset kasvavat sietämättömiksi",
        "Ei pidä siitä, että samanaikaisia tehtäviä on paljon, varsinkaan kun aikaa on vähän",
        "Tuntee itsensä vähemmän energiseksi kuin muut ja suosii rauhallisempaa tai tasaisempaa työtahtia",
        "Voi väsyä tai uupua, kun työkuorma on suuri, ja tarvitsee aikaa palautumiseen"
      ]
    },
    "focusedOnResults": {
      "id": "focusedOnResults",
      "title": "Tulossuuntautunut",
      "summary": "Mittaa keskittymistä tavoitteiden ja tulosten saavuttamiseen tehokkaasti ja pragmaattisesti.",
      "sampleItems": [
        "Keskityn tulosten saavuttamiseen",
        "Teen kaikkeni saavuttaakseni tulokset"
      ],
      "high": [
        "Nauttii vastuusta tavoitteiden saavuttamisessa ja varmistaa, että ne toteutuvat",
        "Haluaa suorittaa käsillä olevan tehtävän nopeasti, tehokkaasti ja pragmaattisesti",
        "Työskentelee tavoitteet mielessä ja tekee kaikkensa niiden saavuttamiseksi",
        "Pyrkii usein saamaan asiat valmiiksi kiireellä ja saattaa ohittaa tärkeitä sivuseikkoja"
      ],
      "low": [
        "Etsii täydellistä ratkaisua ja on valmis kulkemaan sen löytämiseksi kiertoteitä",
        "Kiinnostuu sivuseikoista ja poikkeaa usein aiheesta, mikä voi johtaa uusiin oivalluksiin",
        "Tuottaa mieluummin aukottomia tuloksia ja poistaa kaikki riskit kuin kiirehtii",
        "Saattaa menettää mahdollisuuksia toimimalla liian hitaasti tai eksymällä yksityiskohtiin"
      ]
    },
    "imaginative": {
      "id": "imaginative",
      "title": "Mielikuvituksellinen",
      "summary": "Mittaa mieltymystä luoviin, epätavallisiin ja innovatiivisiin ideoihin sekä lähestymistapoihin ongelmanratkaisussa.",
      "sampleItems": [
        "Tuotan paljon uusia ideoita",
        "Kehitän kekseliäitä ratkaisuja"
      ],
      "high": [
        "Keksii usein uusia, tuoreita ja luovia oivalluksia",
        "Pitää aivoriihityöskentelystä ja tuo esiin lukuisia ideoita",
        "Nauttii työskentelystä luovissa, innovatiivisissa ympäristöissä",
        "Jotkut hänen ideoistaan saattavat olla vähemmän käytännöllisiä tai realistisia",
        "Voi joskus hukuttaa muut ideoidensa uutuudella ja runsaudella"
      ],
      "low": [
        "Kehittää melko tavanomaisia ideoita",
        "Pitäytyy mieluiten hyviksi havaituissa ja koetelluissa ratkaisuissa",
        "On maanläheinen ja pitää itseään vähemmän luovana",
        "Kehittää ja tukee todennäköisemmin muiden ideoita kuin keksii paljon omia",
        "Hänen ideansa pohjautuvat todennäköisesti aiempaan työhön eivätkä ole täysin uusia"
      ]
    },
    "openToChange": {
      "id": "openToChange",
      "title": "Avoin muutokselle",
      "summary": "Mittaa avoimuutta uusille ideoille, muutokselle ja vaihtoehtoisille lähestymistavoille.",
      "sampleItems": [
        "Nautin uusien ideoiden tutkimisesta",
        "Olen avoin muutokselle"
      ],
      "high": [
        "On innoissaan uusista mahdollisuuksista ja haasteista",
        "Kokee muutoksen virkistävänä ja motivoivana",
        "Tukee muutosaloitteita ja kannustaa muita tekemään samoin",
        "Sopeutuu nopeasti uusiin tilanteisiin ja suhtautuu joustavasti työoloihin",
        "Voi tulla levottomaksi tai turhautuneeksi, jos muutos ei tapahdu tarpeeksi nopeasti"
      ],
      "low": [
        "Pitää vakaudesta ja selkeästä suunnasta",
        "Pyrkii säilyttämään nykytilan ja välttää radikaaleja muutoksia",
        "Tarvitsee aikaa sopeutuakseen, kun jotain uutta otetaan käyttöön",
        "Voi vastustaa merkittäviä muutoksia tai kokea odottamattomat käänteet epämukavina"
      ]
    },
    "persuasive": {
      "id": "persuasive",
      "title": "Suostutteleva",
      "summary": "Mittaa taipumusta vaikuttaa muihin perusteluilla ja omalla persoonallaan.",
      "sampleItems": [
        "Nautin idean myymisestä",
        "Neuvotteleminen ihmisten kanssa tuntuu minusta luontevalta"
      ],
      "high": [
        "Nauttii omien suunnitelmiensa edistämisestä ja muiden mielipiteiden muuttamisesta",
        "Rakentaa vakuuttavia perusteluja",
        "Osallistuu mielellään neuvotteluihin ja kokee olevansa niissä tehokas",
        "Tuntee olonsa mukavaksi kylmäsoittomyyntitilanteissa",
        "Muut saattavat joskus pitää häntä manipuloivana tai ovelana"
      ],
      "low": [
        "Luottaa vahvasti faktoihin ja lukuihin edistääkseen suunnitelmia tai asioita",
        "Hyväksyy toisten uskomukset varsin helposti haastamatta niitä",
        "Antaa ihmisille tilaa tehdä omat päätöksensä",
        "Tuntee olonsa epämukavaksi myyntitehtävissä tai vaikeissa neuvotteluissa"
      ]
    },
    "prudent": {
      "id": "prudent",
      "title": "Harkitseva",
      "summary": "Mittaa taipumusta pohtia omien tekojen mahdollisia seurauksia ja katsoa asioita tulevaisuuden näkökulmasta.",
      "sampleItems": [
        "Tarkastelen asioita laajasta näkökulmasta",
        "Otan huomioon suunnitelmien ja toimien laajemmat seuraukset"
      ],
      "high": [
        "Harkitsee erilaisten ratkaisujen käytännöllisyyttä",
        "Havaitsee suunnitelman edut ja haitat",
        "Tunnistaa mahdolliset riskit ja ottaa huomioon monenlaisia tekijöitä",
        "Huomioi aina pitkän aikavälin näkökulman mielipidettä muodostaessaan"
      ],
      "low": [
        "Käyttää harvoin aikaa toimien pitkän aikavälin vaikutusten pohtimiseen",
        "Keskittyy enemmän lyhyen aikavälin kiireellisiin asioihin ja on melko reaktiivinen ja hätäinen",
        "Kiinnittää vähemmän huomiota suunnitelman mahdollisten riskien tai haittojen tunnistamiseen",
        "Jättää usein huomiotta omien tekojensa laajemmat vaikutukset"
      ]
    },
    "sociable": {
      "id": "sociable",
      "title": "Seurallinen",
      "summary": "Mittaa mieltymystä sosiaaliseen vuorovaikutukseen ja kanssakäymiseen muiden kanssa.",
      "sampleItems": [
        "Tapaan mielelläni uusia ihmisiä",
        "Tunnen oloni mukavaksi sosiaalisissa tilanteissa"
      ],
      "high": [
        "Nauttii toisten kanssa keskustelemisesta ja on melko ulospäinsuuntautunut",
        "Hänellä on laaja kontaktiverkosto, ja hän pitää yhteyttä sen jäseniin",
        "Saa muut helposti mukaan keskusteluun ja saa heidät tuntemaan olonsa kotoisaksi",
        "Voi puhua liikaa tai ottaa liikaa tilaa sosiaalisissa tilanteissa, mikä voi toisinaan tehdä muut kärsimättömiksi"
      ],
      "low": [
        "Suosii pienempiä, intiimimpiä ryhmiä",
        "Tuntee olonsa mukavammaksi kahden kesken kuin suurissa ryhmissä",
        "On yleensä hiljainen eikä aloita keskustelua",
        "Ei tunne voimakasta tarvetta sosiaaliseen vuorovaikutukseen tai kontaktien luomiseen"
      ]
    },
    "socially_confident": {
      "id": "socially_confident",
      "title": "Sosiaalisesti itsevarma",
      "summary": "Mittaa koettua itsevarmuutta ja itsenäisyyttä vuorovaikutuksessa muiden kanssa ja sosiaalisissa tilanteissa.",
      "sampleItems": [
        "Olen itsevarma, kun tapaan uusia ihmisiä",
        "Esiintyminen muiden edessä tuntuu minusta luontevalta"
      ],
      "high": [
        "Ottaa luontevasti johtajan roolin sosiaalisissa tilanteissa",
        "Osaa esitellä ideoita suurille ryhmille itsevarmasti",
        "Tuntee olonsa mukavaksi virallisissa tilanteissa ja nauttii sosiaalisista tapahtumista",
        "Voi vaikuttaa liian itsevarmalta tai jopa ylimieliseltä joissakin tilanteissa"
      ],
      "low": [
        "Hänen voi olla vaikea ottaa puheenvuoroa tai esittää ideoitaan ryhmässä",
        "Tuntee olonsa hermostuneeksi tai epävarmaksi tavatessaan uusia ihmisiä tai virallisissa tilanteissa",
        "Tuntee olonsa epämukavaksi suurissa ryhmissä tai julkisissa puhetilanteissa"
      ]
    },
    "systematic": {
      "id": "systematic",
      "title": "Systemaattinen",
      "summary": "Mittaa mieltymystä jäsenneltyyn lähestymistapaan, suunnitteluun ja selkeisiin prioriteetteihin.",
      "sampleItems": [
        "Suunnittelen asiat mielelläni etukäteen",
        "Olen hyvin järjestelmällinen"
      ],
      "high": [
        "Suunnittelee asiat yleensä yksityiskohtaisesti ja pysyy hyvin järjestelmällisenä",
        "Asettaa realistiset prioriteetit ja pysyy aikatauluissa",
        "Lähestyy kaikkea loogisesti ja järjestelmällisesti",
        "Voi muuttua liian jäykäksi tai joustamattomaksi, mikä voi aiheuttaa viiveitä, kun vastaan tulee muutoksia"
      ],
      "low": [
        "On lähestymistavaltaan joustavampi ja spontaanimpi",
        "Ei pidä siitä, että hänet sidotaan jäykkiin aikatauluihin",
        "Tekee joskus työt kiireellä miettimättä kaikkea loppuun asti",
        "Ei ehkä ole yhtä tarkka tai järjestelmällinen kuin muut"
      ]
    }
  }
};

export default dictionary;
